// www/src/pages/services/sextforce/SextforceLiveStreamHismith.tsx
import AddIcon from '@mui/icons-material/Add';
import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, Button, Grid, Skeleton, Stack, Theme, Typography, useTheme } from '@mui/material';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useMemo, useState } from 'react';
import BackNavigationButton from '../../../components/common/BackNavigationButton';
import LargeMaterialUISwitch from '../../../components/common/LargeMaterialUISwitch';
import PageContainer from '../../../components/common/PageContainter';
import StyledCard from '../../../components/common/StyledCard';
import StyledCardTitleBar from '../../../components/common/StyledCardTitleBar';
import AddDeviceDialog from '../../../components/services/sextforce/liveStreamHismith/AddDeviceDialog';
import DeviceDetails from '../../../components/services/sextforce/liveStreamHismith/DeviceDetails';
import DeviceIdleSettings from '../../../components/services/sextforce/liveStreamHismith/DeviceIdleSettings';
import TipRangesEditor from '../../../components/services/sextforce/liveStreamHismith/TipRangesEditor';
import useHismith, { deviceLabel } from '../../../hooks/useHismith';
import useSubscriber from '../../../hooks/useSubscriber';
import { SettingsContext } from '../../../store/SettingsContext';
import { isSextforceActive } from '../../../utils/common';

const WATCH_WINDOW_MS = 60 * 1000;

const SextforceLiveStreamHismith = () => {
    const settingsContext = useContext(SettingsContext);
    const theme: Theme = useTheme();
    const dialog = useDialog();
    const { enqueueSnackbar } = useSnackbar();

    const { data: subscriber, isLoading: isSubscriberLoading } = useSubscriber();

    const sextforceActive = useMemo(() => Boolean(subscriber && isSextforceActive(subscriber)), [subscriber]);

    const [serviceActive, setServiceActive] = useState<boolean>(false);
    const [watchUntil, setWatchUntil] = useState<number>(0);
    const [dialogMode, setDialogMode] = useState<'add' | 'replace' | null>(null);
    const [replacingDeviceId, setReplacingDeviceId] = useState<string | null>(null);
    const [testingDeviceId, setTestingDeviceId] = useState<string | null>(null);

    // Poll while something is connecting or an action was just triggered. The flag is state so the
    // hook call below never reads its own result before it is declared.
    const [now, setNow] = useState<number>(Date.now());
    const [shouldPoll, setShouldPoll] = useState<boolean>(false);

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 5_000);

        return () => clearInterval(timer);
    }, []);

    const {
        data: devices,
        isLoading: isDevicesLoading,
        getServiceActive,
        setServiceActive: saveServiceActive,
        isServiceStatusLoading,
        addDevice,
        isAddingDevice,
        updateDevice,
        isUpdatingDevice,
        deleteDevice,
        isDeletingDevice,
        sendTestCommand,
    } = useHismith(shouldPoll);

    useEffect(() => {
        const connecting = (devices ?? []).some(device => device.status?.state === 'connecting');

        setShouldPoll(connecting || now < watchUntil);
    }, [devices, now, watchUntil]);

    const watch = () => setWatchUntil(Date.now() + WATCH_WINDOW_MS);

    useEffect(() => {
        if (!sextforceActive) {
            setServiceActive(false);

            return;
        }

        getServiceActive()
            .then(active => setServiceActive(active))
            .catch(() => setServiceActive(false));
    }, [sextforceActive]);

    const handleAddOrReplace = async (link: string, nickName: string) => {
        if (dialogMode === 'replace' && replacingDeviceId) {
            await updateDevice(replacingDeviceId, { link });
            enqueueSnackbar('Link updated. Connecting…', { variant: 'success', autoHideDuration: 3000 });
        } else {
            await addDevice(link, nickName);
            enqueueSnackbar('Device added. Connecting…', { variant: 'success', autoHideDuration: 3000 });
        }

        watch();
    };

    const handleTest = async (deviceId: string) => {
        setTestingDeviceId(deviceId);

        try {
            await sendTestCommand(deviceId);
            watch();
            enqueueSnackbar('Test sent. The machine runs at 60% for 3 seconds once connected.', {
                variant: 'success',
                autoHideDuration: 4000,
            });
        } catch {
            enqueueSnackbar('Could not send the test. Try again in a moment.', { variant: 'error', autoHideDuration: 3000 });
        } finally {
            setTestingDeviceId(null);
        }
    };

    const handleDelete = (deviceId: string, label: string) => {
        dialog
            .confirm({
                title: 'Remove device',
                message: `Remove ${label}? Tip ranges that target this device are deleted too. "Any device" ranges are kept.`,
                ok: { text: 'Remove', variant: 'contained', color: 'error' },
                cancel: { text: 'Cancel', variant: 'outlined' },
            })
            .then(async () => {
                const ok = await deleteDevice(deviceId);

                enqueueSnackbar(ok ? 'Device removed.' : 'Could not remove the device.', {
                    variant: ok ? 'success' : 'error',
                    autoHideDuration: 3000,
                });
            })
            .catch(() => {
                // cancelled
            });
    };

    return (
        <PageContainer>
            {subscriber && '_id' in subscriber && subscriber._id && (
                <>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        <BackNavigationButton url={`/subscribers/${subscriber._id}/${settingsContext.services.sextforce.homeUrl}`} />
                        Hismith Remote Play for {subscriber.username}
                    </Typography>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar title="Hismith Remote Play" />
                    </StyledCard>
                    <StyledCard>
                        <Typography gutterBottom>
                            Let your fans drive your Hismith machine with tips during your live streams. When enabled, each tip that falls
                            inside one of your tip ranges sets the machine's speed for the configured duration, then it returns to its idle
                            speed.
                        </Typography>
                        <Typography gutterBottom>
                            To connect a machine, open it in the HISMITH app, choose <b>Remote Play</b>, copy the link and paste it below.
                            Keep the Remote Play screen open while you stream. If the app leaves Remote Play the link stops working and the
                            device shows as "App offline" here; paste a fresh link to reconnect.
                        </Typography>
                        <Typography>
                            Use the Test button to check a device before going live. Devices connect on demand, so a test works even when
                            you are not streaming.
                        </Typography>
                    </StyledCard>

                    <StyledCard>
                        <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center">
                            <Grid item xs>
                                Hismith Remote Play is currently{' '}
                                {serviceActive ? (
                                    <span style={{ color: theme.palette.success.dark }}>running</span>
                                ) : (
                                    <span style={{ color: theme.palette.error.main }}>not running</span>
                                )}
                                .
                            </Grid>
                            <Grid item xs="auto">
                                {isSubscriberLoading || isServiceStatusLoading ? (
                                    <Skeleton width={'115px'} height={'51px'} />
                                ) : (
                                    <LargeMaterialUISwitch
                                        checked={serviceActive}
                                        disabled={!sextforceActive}
                                        onChange={e => {
                                            const next = e.target.checked;

                                            if (!sextforceActive || next === serviceActive) return;

                                            saveServiceActive(next).then(success => success && setServiceActive(next));
                                        }}
                                        color="primary"
                                    />
                                )}
                            </Grid>
                        </Grid>

                        {!sextforceActive && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                Sextforce service must be active to use this feature
                            </Alert>
                        )}
                    </StyledCard>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar title="My Hismith Devices" isLoading={isDevicesLoading} />
                    </StyledCard>
                    <StyledCard>
                        <Stack direction="column" spacing={2} mb={2}>
                            {devices && devices.length > 0 ? (
                                devices.map(device => (
                                    <DeviceDetails
                                        key={device.deviceId}
                                        device={device}
                                        isTesting={testingDeviceId === device.deviceId}
                                        onTest={() => handleTest(device.deviceId)}
                                        onUpdateLink={() => {
                                            setReplacingDeviceId(device.deviceId);
                                            setDialogMode('replace');
                                        }}
                                    />
                                ))
                            ) : (
                                <Typography>No Hismith devices connected yet.</Typography>
                            )}
                        </Stack>

                        <LoadingButton
                            loading={isAddingDevice}
                            variant="contained"
                            color="secondary"
                            fullWidth
                            size="large"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                setReplacingDeviceId(null);
                                setDialogMode('add');
                            }}
                        >
                            <span>Add Hismith Device</span>
                        </LoadingButton>
                    </StyledCard>

                    <AddDeviceDialog
                        open={dialogMode !== null}
                        mode={dialogMode ?? 'add'}
                        isSaving={isAddingDevice || isUpdatingDevice}
                        onClose={() => setDialogMode(null)}
                        onSubmit={handleAddOrReplace}
                    />

                    {devices?.map(device => (
                        <div key={`idle-${device.deviceId}`}>
                            <StyledCard noCard noBottomMargin>
                                <StyledCardTitleBar
                                    title={`${deviceLabel(device)} Idle Speed`}
                                    isLoading={isUpdatingDevice || isDeletingDevice}
                                    badge={
                                        <Button
                                            variant="contained"
                                            color="error"
                                            size="small"
                                            disabled={isDeletingDevice}
                                            onClick={() => handleDelete(device.deviceId, deviceLabel(device))}
                                        >
                                            Remove
                                        </Button>
                                    }
                                />
                            </StyledCard>
                            <StyledCard>
                                <DeviceIdleSettings
                                    device={device}
                                    isSaving={isUpdatingDevice}
                                    onSave={(idleSpeed, idleVibration) => {
                                        updateDevice(device.deviceId, { idleSpeed, idleVibration }).catch(error =>
                                            enqueueSnackbar(`Failed to save idle speed: ${error.message}`, { variant: 'error' }),
                                        );
                                    }}
                                />
                            </StyledCard>
                        </div>
                    ))}

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar title="Tip Ranges" />
                    </StyledCard>
                    <StyledCard>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            Each range sets the speed for tips between its start and end amount. A range targets any device or one specific
                            device; a device-specific range wins over an "Any device" range for that device.
                        </Typography>
                        <TipRangesEditor devices={devices ?? []} />
                    </StyledCard>
                </>
            )}
        </PageContainer>
    );
};

export default SextforceLiveStreamHismith;
